// HB-1942: Build a stable, low-cardinality fingerprint string of the form
// "METHOD path STATUS". Async-generator transpilation collapses every fetch
// rejection's backtrace to the same four minified vendor frames, so HB groups
// every async-fetch error into one bucket. Passing this string as the top-level
// Honeybadger `fingerprint` option splits buckets per call site.
//
// Contract: TOTAL — never throws. Returns null when status is missing (caller
// must skip the fingerprint so error-boundary catches with no HTTP status don't
// collapse into a single bucket). The malformed-data fallback in
// `reportRequestError` relies on this helper being throw-free.
const buildRequestFingerprint = (method, url, status) => {
  if (status == null) return null;
  // Trim before length-checking and uppercasing so a non-canonical caller
  // passing "  get  " emits "GET …", not "  GET   …" with embedded whitespace
  // (which would silently widen fingerprint cardinality).
  const trimmedMethod = typeof method === "string" ? method.trim() : "";
  const m = trimmedMethod.length > 0 ? trimmedMethod.toUpperCase() : "UNKNOWN";
  if (url == null || url === "") return `${m} /unknown ${status}`;
  let path = "/unknown";
  try {
    const base =
      typeof window !== "undefined" && window.location ? window.location.origin : "http://x";
    const parsed = url instanceof URL ? url : new URL(String(url), base);
    path = parsed.pathname || "/unknown";
    // Two-pass normalization:
    //   1. Pure-numeric segments always collapse — even short ones like /42
    //      (existing routes such as /api/products/42/cp would otherwise leak
    //      one Honeybadger bucket per record). The `[./]|$` lookahead also
    //      catches Rails-style suffixes: /api/orders/9.json → /api/orders/:id.json.
    //   2. Mixed digit+letter segments collapse only when length > 3, so
    //      version-like literals (/v1, /v2) survive while prefixed session
    //      IDs (/sess-1) and hex-slug IDs (/0123456789abcdef-some-slug) are
    //      caught. The leading `/` is included in the matched length.
    path = path
      .replace(/\/\d+(?=[./]|$)/g, "/:id")
      .replace(/\/[A-Za-z0-9_-]*\d[A-Za-z0-9_-]*(?=[./]|$)/g, segment =>
        segment.length > 3 ? "/:id" : segment
      );
  } catch {
    path = "/unknown";
  }
  return `${m} ${path} ${status}`;
};

export default buildRequestFingerprint;
